import {
  calculateLoanSummary,
  roundCurrency,
  parseNumber,
  formatAxisCurrency,
  formatCurrency,
} from "@/utils/finance";

import {
  Scale,
  Landmark,
  Wallet,
  Percent,
  BadgeCheck,
  Calculator,
} from "lucide-react";

import InsightCard from "@/components/calculator/InsightCard";

import DataTable from "@/components/calculator/DataTable";

import {
  ACTIONS,
  CHARTS,
  FIELD_TYPES,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

const lenderComparisonCalculatorConfig = {
  id: "lender-comparison",

  shortTitle: "Comparison",

  title: "Lender Comparison Calculator",

  subtitle:
    "Compare multiple lenders based on EMI, interest payable, processing fee and total borrowing cost.",

  description:
    "Evaluate multiple home loan offers side-by-side to identify the lender with the lowest overall borrowing cost.",

  icon: Scale,

  keywords: [
    "Lender Comparison",
    "Home Loan",
    "Bank Comparison",
    "EMI Comparison",
    "Interest Comparison",
    "Processing Fee",
    "Loan Cost",
  ],

  fields: [
    /* -------------------------------------------------- */
    /* Loan Details                                       */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,

      title: "Loan Details",
    },

    {
      key: "loanAmount",

      label: "Loan Amount",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 5000000,

      min: 100000,

      max: 500000000,

      step: 50000,
    },

    {
      key: "tenureYears",

      label: "Loan Tenure",

      type: FIELD_TYPES.NUMBER,

      suffix: "Years",

      defaultValue: 20,

      min: 1,

      max: 35,
    },

    /* -------------------------------------------------- */
    /* Lenders                                             */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,

      title: "Lender Details",
    },

    {
      key: "lenders",

      label: "Lenders",

      type: FIELD_TYPES.ARRAY,

      defaultValue: [
        {
          name: "Lender A",
          annualRate: 8.3,
          processingFee: 10000,
        },
        {
          name: "Lender B",
          annualRate: 8.55,
          processingFee: 5000,
        },
        {
          name: "Lender C",
          annualRate: 8.75,
          processingFee: 0,
        },
      ],

      addButtonLabel: "Add Lender",

      columns: {
        md: 2,
        xl: 3,
      },

      itemFields: [
        {
          key: "name",

          label: "Lender",

          type: "text",

          defaultValue: "",
        },

        {
          key: "annualRate",

          label: "Interest Rate",

          type: FIELD_TYPES.PERCENT,

          defaultValue: 8.5,

          min: 5,

          max: 18,

          step: 0.05,
        },

        {
          key: "processingFee",

          label: "Processing Fee",

          type: FIELD_TYPES.CURRENCY,

          defaultValue: 0,

          min: 0,

          max: 500000,

          step: 1000,
        },
      ],
    },
  ],

  summaryCards: [
    {
      title: "Best Lender",

      key: "bestLender",

      displayKey: "name",

      type: RESULT_TYPES.TEXT,

      description: "Lender with the lowest overall borrowing cost.",

      icon: BadgeCheck,
    },

    {
      title: "Costliest Lender",
      key: "costliestLender",
      type: RESULT_TYPES.TEXT,
      icon: Landmark,
    },

    {
      title: "Lowest EMI",

      key: "lowestEmi",

      type: RESULT_TYPES.CURRENCY,

      description: "Lowest monthly EMI among all lenders.",

      icon: Wallet,
    },

    {
      title: "Lowest Interest",

      key: "lowestInterest",

      type: RESULT_TYPES.CURRENCY,

      description: "Lowest total interest payable.",

      icon: Percent,
    },

    {
      title: "Lowest Total Cost",

      key: "lowestTotalCost",

      type: RESULT_TYPES.CURRENCY,

      description: "Interest plus processing fee.",

      icon: Calculator,
    },

    {
      title: "Maximum Savings",

      key: "maximumSavings",

      type: RESULT_TYPES.CURRENCY,

      description: "Savings compared to the costliest lender.",

      icon: Landmark,
    },
  ],

  charts: [
    {
      id: "emi-comparison",

      title: "Monthly EMI Comparison",

      description: "Compare monthly EMI offered by each lender.",

      type: CHARTS.BAR,

      resultsKey: "emiComparison",

      minWidth: 600,

      xAxis: {
        dataKey: "name",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      bars: [
        {
          dataKey: "emi",

          name: "Monthly EMI",
        },
      ],
    },

    {
      id: "interest-comparison",

      title: "Total Interest Comparison",

      description: "Compare total interest payable for each lender.",

      type: CHARTS.BAR,

      resultsKey: "interestComparison",

      minWidth: 600,

      xAxis: {
        dataKey: "name",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      bars: [
        {
          dataKey: "totalInterest",

          name: "Total Interest",
        },
      ],
    },

    {
      id: "total-cost-comparison",

      title: "Overall Borrowing Cost",

      description: "Interest plus processing fee for every lender.",

      type: CHARTS.BAR,

      resultsKey: "totalCostComparison",

      minWidth: 600,

      xAxis: {
        dataKey: "name",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      bars: [
        {
          dataKey: "totalCost",

          name: "Total Cost",
        },
      ],
    },
  ],

  tables: [
    {
      title: "Lender Comparison",

      component: DataTable,

      dataKey: "comparison",

      columns: [
        {
          key: "rank",
          label: "Rank",
          type: "number",
        },

        {
          key: "name",
          label: "Lender",
        },

        {
          key: "annualRate",
          label: "Rate",
          type: "percent",
        },

        {
          key: "processingFee",
          label: "Processing Fee",
          type: "currency",
        },

        {
          key: "emi",
          label: "EMI",
          type: "currency",
        },

        {
          key: "totalInterest",
          label: "Interest",
          type: "currency",
        },

        {
          key: "totalCost",
          label: "Total Cost",
          type: "currency",
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const { loanAmount, tenureYears, lenders = [] } = values;

    /* -------------------------------------------------- */
    /* Lender Comparison                                  */
    /* -------------------------------------------------- */

    const comparison = lenders.map((lender) => {
      const loanSummary = calculateLoanSummary({
        principal: loanAmount,
        annualRate: lender.annualRate,
        tenureYears,
      });

      const processingFee = parseNumber(lender.processingFee);

      const totalCost = roundCurrency(
        loanSummary.totalInterest + processingFee,
      );

      return {
        name: lender.name,
        annualRate: lender.annualRate,
        processingFee,

        emi: loanSummary.emi,
        totalInterest: loanSummary.totalInterest,
        totalPayment: loanSummary.totalPayment,

        totalCost,
      };
    });

    /* -------------------------------------------------- */
    /* Ranking                                             */
    /* -------------------------------------------------- */

    const rankedComparison = [...comparison]
      .sort((a, b) => a.totalCost - b.totalCost)
      .map((item, index) => ({
        ...item,
        rank: index + 1,
      }));

    const bestLender = rankedComparison[0] ?? null;
    const worstLender = rankedComparison[rankedComparison.length - 1] ?? null;

    const lowestEmi = comparison.length
      ? Math.min(...comparison.map((x) => x.emi))
      : 0;

    const lowestInterest = comparison.length
      ? Math.min(...comparison.map((x) => x.totalInterest))
      : 0;

    const lowestTotalCost = comparison.length
      ? Math.min(...comparison.map((x) => x.totalCost))
      : 0;

    const maximumSavings =
      worstLender && bestLender
        ? roundCurrency(worstLender.totalCost - bestLender.totalCost)
        : 0;

    /* -------------------------------------------------- */
    /* Recommendation                                      */
    /* -------------------------------------------------- */

    const recommendation = bestLender
      ? {
          title: "Best Overall Value",

          variant: "success",

          description: `${bestLender.name} offers the lowest overall borrowing cost after considering both interest and processing fees.`,
        }
      : null;

    /* -------------------------------------------------- */
    /* Summary                                             */
    /* -------------------------------------------------- */

    const summary = {
      loanAmount,

      tenureYears,

      lenderCount: comparison.length,

      bestLender: bestLender?.name ?? "-",

      costliestLender: worstLender?.name,

      lowestEmi,

      lowestInterest,

      lowestTotalCost,

      maximumSavings,

      recommendation,
    };

    /* -------------------------------------------------- */
    /* Charts                                              */
    /* -------------------------------------------------- */

    const charts = {
      emiComparison: rankedComparison.map((item) => ({
        name: item.name,
        emi: item.emi,
      })),

      interestComparison: rankedComparison.map((item) => ({
        name: item.name,
        totalInterest: item.totalInterest,
      })),

      totalCostComparison: rankedComparison.map((item) => ({
        name: item.name,
        totalCost: item.totalCost,
      })),
    };

    /* -------------------------------------------------- */
    /* Tables                                              */
    /* -------------------------------------------------- */

    const tables = {
      comparison: rankedComparison,
    };

    /* -------------------------------------------------- */
    /* Return                                              */
    /* -------------------------------------------------- */

    return {
      summary,
      charts,
      tables,

      metadata: {
        generatedAt: new Date().toISOString(),

        loanAmount,

        tenureYears,

        lenderCount: comparison.length,
      },
    };
  },

  exportExcel: ({ results }) => ({
    Comparison: results.tables.comparison ?? [],
  }),

  renderInsights: ({ results }) => {
    const { summary, tables } = results;

    const {
      recommendation,
      loanAmount,
      tenureYears,
      lenderCount,
      bestLender,
      costliestLender,
      lowestEmi,
      lowestInterest,
      lowestTotalCost,
      maximumSavings,
    } = summary;

    const comparison = tables.comparison ?? [];

    const winner = comparison.find((lender) => lender.rank === 1);

    const costliest = costliestLender;

    return (
      <>
        {recommendation && (
          <InsightCard
            variant={recommendation.variant}
            title={recommendation.title}
          >
            <p>{recommendation.description}</p>
          </InsightCard>
        )}

        <InsightCard title="Loan Comparison Summary" variant="info">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Loan Amount:
              <strong className="ml-1">{formatCurrency(loanAmount)}</strong>
            </li>

            <li>
              Loan Tenure:
              <strong className="ml-1">{tenureYears} Years</strong>
            </li>

            <li>
              Lenders Compared:
              <strong className="ml-1">{lenderCount}</strong>
            </li>

            <li>
              Best Lender:
              <strong className="ml-1">{winner?.name ?? "-"}</strong>
            </li>

            <li>
              Lowest EMI:
              <strong className="ml-1">{formatCurrency(lowestEmi)}</strong>
            </li>

            <li>
              Lowest Interest Payable:
              <strong className="ml-1">{formatCurrency(lowestInterest)}</strong>
            </li>

            <li>
              Lowest Total Cost:
              <strong className="ml-1">
                {formatCurrency(lowestTotalCost)}
              </strong>
            </li>
          </ul>
        </InsightCard>

        {winner && (
          <InsightCard title="Best Lender Analysis" variant="success">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>{winner.name}</strong> offers the lowest overall
                borrowing cost.
              </li>

              <li>
                Interest Rate:
                <strong className="ml-1">{winner.annualRate}%</strong>
              </li>

              <li>
                Monthly EMI:
                <strong className="ml-1">{formatCurrency(winner.emi)}</strong>
              </li>

              <li>
                Total Interest:
                <strong className="ml-1">
                  {formatCurrency(winner.totalInterest)}
                </strong>
              </li>

              <li>
                Processing Fee:
                <strong className="ml-1">
                  {formatCurrency(winner.processingFee)}
                </strong>
              </li>

              <li>
                Total Borrowing Cost:
                <strong className="ml-1">
                  {formatCurrency(winner.totalCost)}
                </strong>
              </li>
            </ul>
          </InsightCard>
        )}

        {costliest && maximumSavings > 0 && (
          <InsightCard title="Potential Savings" variant="warning">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Choosing <strong>{bestLender.name}</strong> instead of{" "}
                <strong>{costliest}</strong> can reduce your borrowing cost by
                <strong className="ml-1">
                  {formatCurrency(maximumSavings)}
                </strong>
                .
              </li>

              <li>
                A lower interest rate usually has a much bigger impact on total
                cost than a small difference in processing fees.
              </li>

              <li>
                Always compare the complete loan cost rather than only the
                advertised interest rate.
              </li>
            </ul>
          </InsightCard>
        )}

        <InsightCard title="Recommendations" variant="default">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Compare total borrowing cost instead of selecting a lender based
              only on EMI.
            </li>

            <li>
              Review processing charges, legal fees and other one-time charges
              before making the final decision.
            </li>

            <li>
              Check for foreclosure, part-payment and balance transfer charges.
            </li>

            <li>
              Compare customer service, disbursement time and lender policies in
              addition to pricing.
            </li>

            <li>
              Negotiate the interest rate if you have a strong credit profile.
            </li>
          </ul>
        </InsightCard>
      </>
    );
  },
};

export default lenderComparisonCalculatorConfig;
